import { z } from "zod";

/**
 * Modules a membership may open (ADR-088).
 *
 * Identifiers are stable and independent of route paths. Profile and auth
 * session endpoints never require a grant. SUPER_ADMIN bypasses the check.
 */
export const APP_VIEWS = [
  "DASHBOARD",
  "INCIDENTS",
  "DEVICES",
  "ASSETS",
  "SITES",
  "GATEWAYS",
  "LOAN_AGREEMENTS",
  "SERVICE_AGREEMENTS",
  "SUPPORTED_DEVICES",
  "USERS",
  "ALERT_RULES",
  "CONTACTS",
  "WEBHOOK_EVENTS",
  "AUDIT",
] as const;

export const appViewSchema = z.enum(APP_VIEWS);
export type AppView = z.infer<typeof appViewSchema>;

/** Tenant membership roles that carry an editable view allowlist. */
export type MembershipAssignableRole = "USER" | "ADMIN";

/** Views every org member may be granted. */
export const OPERATIONAL_VIEWS: readonly AppView[] = [
  "DASHBOARD",
  "INCIDENTS",
  "DEVICES",
  "ASSETS",
  "SITES",
  "GATEWAYS",
  "LOAN_AGREEMENTS",
  "SERVICE_AGREEMENTS",
  "SUPPORTED_DEVICES",
];

/** Extra views only an ADMIN (or SUPER_ADMIN) may hold. */
export const ADMIN_ONLY_VIEWS: readonly AppView[] = [
  "USERS",
  "ALERT_RULES",
  "CONTACTS",
  "WEBHOOK_EVENTS",
  "AUDIT",
];

/**
 * Maximum set of views assignable for a role. SUPER_ADMIN is not granted
 * through membership; the guard bypasses the allowlist instead.
 */
export function viewsForRole(role: MembershipAssignableRole): AppView[] {
  if (role === "ADMIN") {
    return [...OPERATIONAL_VIEWS, ...ADMIN_ONLY_VIEWS];
  }

  return [...OPERATIONAL_VIEWS];
}

/** Keeps only views still allowed after a role change (e.g. ADMIN → USER). */
export function intersectViewsWithRole(
  views: readonly AppView[],
  role: MembershipAssignableRole,
): AppView[] {
  const ceiling = new Set(viewsForRole(role));

  return APP_VIEWS.filter((view) => views.includes(view) && ceiling.has(view));
}
